import { AdminPanelSettings } from "@mui/icons-material";
import { Box } from "@mui/material";
import React, { useCallback, useMemo } from "react";

import { LOCAL_STORAGE_KEY_RECENTLY_CHOSEN_ACCOUNTS } from "@helpers/constants";

import { useLegacyRecentsMigration } from "../../hooks/useLegacyRecentsMigration";
import { SHARED_BOOKMARK_CACHE_IDS } from "../../hooks/useResourceBookmarks";
import { useGetMeQuery } from "../../store/api/usersApi";
import { useAccountsWithMap } from "../../store/hooks/useAccountsWithMap";
import DescriptiveTooltip from "../DescriptiveTooltip";
import ResourceSelector from "../ResourceSelector/ResourceSelector";
import type {
  ResourceItem,
  ResourceSelectorProps,
} from "../ResourceSelector/ResourceSelector";

export type AccountOption = Pick<ResourceItem, "id" | "name" | "fullName"> & {
  /**
   * Adds an "admin" badge to the option. Only meaningful for callers that supply their own `options`.
   */
  isAdmin?: boolean;
};

export interface AccountSelectorProps extends Pick<
  ResourceSelectorProps<AccountOption>,
  "placeholder" | "required" | "error" | "sx" | "loading"
> {
  /** The currently selected account ID */
  value: string | null;
  /** Callback when the selected account changes */
  onChange: (accountId: string | null) => void;
  /** Restricts the account list (and the given `value`'s validity) to accounts where this permission is held. */
  permission?: string;
  options?: AccountOption[];
}

const AdminBadge = () => (
  <DescriptiveTooltip title="You can manage policies across this account">
    <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
      <AdminPanelSettings fontSize="small" color="action" />
    </Box>
  </DescriptiveTooltip>
);

const AccountSelector = React.memo(function AccountSelector({
  value,
  onChange,
  permission,
  options,
  loading: externalLoading = false,
  required,
  error: errorOverride,
  placeholder = "All accounts",
  sx = { width: (theme) => theme.spacing(35) },
}: AccountSelectorProps) {
  const usesSuppliedOptions = options !== undefined;
  const {
    accountsList: loadedAccounts,
    accountsMap: loadedAccountsMap,
    isLoading,
    isFetching,
  } = useAccountsWithMap({
    permission,
    skip: usesSuppliedOptions,
  });
  const suppliedAccountsMap = useMemo(() => {
    const map = new Map<string, AccountOption>();
    options?.forEach((account) => {
      map.set(account.id, account);
      if (account.fullName) map.set(account.fullName, account);
    });
    return map;
  }, [options]);
  const accountsList = options ?? loadedAccounts;
  const accountsMap = usesSuppliedOptions
    ? suppliedAccountsMap
    : loadedAccountsMap;
  const selectorLoading =
    externalLoading || (!usesSuppliedOptions && (isLoading || isFetching));
  const { data: me } = useGetMeQuery();
  const { legacyIds, clearLegacyRecents } = useLegacyRecentsMigration(
    LOCAL_STORAGE_KEY_RECENTLY_CHOSEN_ACCOUNTS,
  );

  // Resolve legacy comma-joined recent IDs into full Account objects so the
  // ResourceSelector can seed them into its shared bookmarks cache.
  const initialRecents = useMemo(() => {
    if (accountsMap.size === 0) return [];
    return legacyIds
      .map((id) => accountsMap.get(id))
      .filter((account): account is AccountOption => Boolean(account));
  }, [accountsMap, legacyIds]);

  const resourceValue = useMemo((): AccountOption | null => {
    if (!value) return null;
    return accountsMap.get(value) ?? { id: value };
  }, [value, accountsMap]);

  const handleChange = useCallback(
    (account: AccountOption | null) => {
      onChange(account ? account.id : null);
    },
    [onChange],
  );

  const handleFreeSoloBlur = useCallback(
    (rawValue: string | null) => {
      if (!rawValue) {
        onChange(null);
        return;
      }
      const account = accountsMap.get(rawValue);
      onChange(account?.id ?? rawValue);
    },
    [accountsMap, onChange],
  );

  const defaultErrorMessage = useMemo(() => {
    if (!value || selectorLoading) return null;
    return accountsMap.has(value) ? null : "Account does not appear to exist";
  }, [value, accountsMap, selectorLoading]);
  const errorMessage =
    errorOverride !== undefined ? errorOverride : defaultErrorMessage;

  return (
    <ResourceSelector<AccountOption>
      resourceType="account"
      value={resourceValue}
      onChange={handleChange}
      defaultOptions={accountsList}
      sandboxId={usesSuppliedOptions ? undefined : me?.account}
      cacheId={SHARED_BOOKMARK_CACHE_IDS.account}
      initialRecents={usesSuppliedOptions ? undefined : initialRecents}
      onInitialRecentsSeeded={
        usesSuppliedOptions ? undefined : clearLegacyRecents
      }
      freeSolo={!usesSuppliedOptions}
      onFreeSoloBlur={usesSuppliedOptions ? undefined : handleFreeSoloBlur}
      label="Account"
      placeholder={placeholder}
      loading={selectorLoading}
      required={required}
      inputLabelProps={{ shrink: true }}
      error={errorMessage}
      sx={sx}
      renderOptionIcon={(option) => (option.isAdmin ? <AdminBadge /> : null)}
    />
  );
});

export { AccountSelector };
